import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import {useCreateTerm, useQueryTerm, useUpdateTerm} from "@/service/term.s";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue, filterDicts, getSelectValues, iso2Date, setValues} from "@/helper/method";
import {TagOut} from "@/widget/TagOut/TagOut";
import {isNumber} from "lodash";


const validationSchema = z.object({
    id: z.unknown().default(0),
    category: z.string({message: "请选择项目名称"}),
    categoryId: z.unknown(),
    kind: z.string({message: "选择政策种类"}),
    kindId: z.unknown(),
    level: z.string({message: "选择政策级别"}),
    levelId: z.unknown(),
    subsidyNum: z.number({message: "请填写获批数量"}),
    suitables: z.string({message: "请选择适用对象"}),
    suitableIds: z.unknown(),
    section: z.string({message: "请选择受理部门"}),
    sectionId: z.unknown(),
    maxSubsidy: z.unknown(),
    subsidyIds: z.unknown(),
    subsidies: z.string({message: "请选择补贴类型"}),
    startDate: z.date().nullable(),
    endDate: z.date().nullable(),
    condition: z.string().nullable(),
    analyze: z.string().nullable(),
    benefits: z.string({message: "请选择优势好处"}),
    benefitIds: z.unknown(),

    tempBenefits: z.array(z.object({label: z.string(), value: z.number()})),
    tempSuitables: z.array(z.object({label: z.string(), value: z.number()})),
    tempSubsidies: z.array(z.object({label: z.string(), value: z.number()})),
});

export type WriteTermForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteTerm = () => {
    const allDict = useAllDict()
    const {termSubsidy, projectType, termLevel, termSection, termKind, industry, termBenefit} = allDict
    const {setToast} = useGlobalActions()
    const {onSearch, id: drawerId, write} = useDrawerModal()
    const {mutateAsync: createTermMutate, isPending: createTermPending} = useCreateTerm()
    const {mutateAsync: updateTermMutate, isPending: updateTermPending} = useUpdateTerm()
    const {data: termData} = useQueryTerm({id: drawerId!}, isNumber(drawerId))
    const term = termData?.data
    const {setDrawerModal} = useGlobalActions()

    const initValues = () => {
        if (write == 'edit') {
            return {
                ...term,
                startDate: iso2Date(term?.startDate), endDate: iso2Date(term?.endDate),
                tempSubsidies: filterDicts(termSubsidy, term?.subsidyIds),
                tempSuitables: filterDicts(industry, term?.suitableIds),
                tempBenefits: filterDicts(termBenefit, term?.benefitIds),
            };
        }
        return {}
    }

    const {
        register,
        control,
        setValue,
        handleSubmit,
        getValues,
        reset,
        formState: {errors},
    } = useForm<WriteTermForm>({
        resolver: zodResolver(validationSchema),
        values: initValues() as WriteTermForm,
        defaultValues,
    });
    const onSubmit = async (values: WriteTermForm) => {
        const result = write == 'add' ? await createTermMutate(values) : await updateTermMutate({...values, id: drawerId})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>项目名称</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={projectType}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("categoryId", option?.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.category}
                                           helperText={errors?.category?.message}
                                           variant="outlined"
                                           placeholder={"选择项目名称"} {...params} />
                            )}
                        />} name={"category"}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>项目种类</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={termKind}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("kindId", option?.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.kind}
                                           helperText={errors?.kind?.message}
                                           variant="outlined"
                                           placeholder={"选择项目种类"} {...params} />
                            )}
                        />} name={"kind"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>级别</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={termLevel}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("levelId", option?.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.level}
                                           helperText={errors?.level?.message}
                                           variant="outlined"
                                           placeholder={"选择级别"} {...params} />
                            )}
                        />} name={"level"}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>受理部门</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={termSection}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("sectionId", option?.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.section}
                                           helperText={errors?.section?.message}
                                           variant="outlined"
                                           placeholder={"选择受理部门"} {...params} />
                            )}
                        />} name={"section"}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>补贴类型</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                multiple={true}
                                value={value || []}
                                options={termSubsidy}
                                disableListWrap={true}
                                renderTags={(values) => {
                                    return values?.map((value: DictPO, index: number) => <TagOut key={index} index={index} label={value.label}/>)
                                }}
                                getOptionLabel={(option: DictPO) => option.label}
                                getOptionKey={(option: DictPO) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                onChange={(_, options) => {
                                    const values = getSelectValues(options, {key: "label", val: 'value'}, {key: "subsidyIds", val: "subsidies"})
                                    setValues(values, setValue as any)
                                    onChange(options)
                                }}
                                disableCloseOnSelect={true}
                                renderInput={(params) => (
                                    <TextField error={!!errors?.subsidies}
                                               sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                               helperText={errors?.subsidies?.message} placeholder={"选择补贴类型"}
                                               {...params} size={'small'}/>
                                )}
                            />
                        }} name={"tempSubsidies"}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true}>
                        <FormLabel>获批企业</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            type={'number'}
                            value={value}
                            onChange={event => onChange(parseInt(event.target.value) || null)}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 10000,
                                        maxLength: 5, inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <>家</>,
                                },
                            }}
                            placeholder="填写获批企业"
                        />} name={'subsidyNum'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true}>
                        <FormLabel>最高奖励</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            type={'number'}
                            value={value}
                            onChange={event => onChange(parseInt(event.target.value) || null)}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 100000000,
                                        maxLength: 9, inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <>元</>,
                                },
                            }}
                            placeholder="填写最高奖励"
                        />} name={'maxSubsidy'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>优势好处</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                multiple={true}
                                value={value || []}
                                options={termBenefit}
                                disableListWrap={true}
                                renderTags={(values) => {
                                    return values?.map((value: DictPO, index: number) => <TagOut key={index} index={index} label={value.label}/>)
                                }}
                                getOptionLabel={(option: DictPO) => option.label}
                                getOptionKey={(option: DictPO) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                onChange={(_, options) => {
                                    const values = getSelectValues(options, {key: "label", val: 'value'}, {key: "benefitIds", val: "benefits"})
                                    setValues(values, setValue as any)
                                    onChange(options)
                                }}
                                disableCloseOnSelect={true}
                                renderInput={(params) => (
                                    <TextField error={!!errors?.subsidies}
                                               sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                               helperText={errors?.subsidies?.message} placeholder={"选择优势好处"}
                                               {...params} size={'small'}/>
                                )}
                            />
                        }} name={"tempBenefits"}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>适用对象</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                multiple={true}
                                value={value || []}
                                options={industry}
                                disableListWrap={true}
                                renderTags={(values) => {
                                    return values?.map((value: DictPO, index: number) => <TagOut key={index} index={index} label={value.label}/>)
                                }}
                                getOptionLabel={(option: DictPO) => option.label}
                                getOptionKey={(option: DictPO) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                onChange={(_, options) => {
                                    const values = getSelectValues(options, {key: "label", val: 'value'}, {key: "suitableIds", val: "suitables"})
                                    setValues(values, setValue as any)
                                    onChange(options)
                                }}
                                disableCloseOnSelect={true}
                                renderInput={(params) => (
                                    <TextField error={!!errors?.subsidies}
                                               sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                               helperText={errors?.subsidies?.message} placeholder={"选择适用对象"}
                                               {...params} size={'small'}/>
                                )}
                            />
                        }} name={"tempSuitables"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>开始时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择开始时间"}
                                customInput={<TextField
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={"startDate"}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>结束时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择结束时间"}
                                customInput={<TextField
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={"endDate"}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>政策解析</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            {...register("analyze")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto'},
                                    inputProps: {maxLength: 1000},
                                },
                            }}
                            placeholder="政策解析"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>申报条件</FormLabel>
                        <TextField
                            multiline
                            {...register("condition")}
                            rows={3}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto'},
                                    inputProps: {maxLength: 1000},
                                },
                            }}
                            placeholder="填写申报条件"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createTermPending || updateTermPending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
